// src/app/modules/employee/Mesrequetes/FeuillesTemps.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    ChevronLeft, ChevronRight, Clock, Edit2, FileText,
    CheckCircle, AlertCircle, Lock, Save, RefreshCw,
    TrendingUp, Calendar, Zap, X, XCircle,
} from "lucide-react";

import "../MesrequetesStyles/FeuillesTemps.css";

import {
    getWorkdaysConfigRequest,
    getCalendarSettingsRequest,
} from "../../../../redux/actions/workDaysActions";

import {
    fetchTimesheetsRequest,
    createTimesheetRequest,
    updateTimesheetRequest,
    fetchTimesheetDaysRequest,
    upsertTimesheetDayRequest,
} from "../../../../redux/actions/timesheetActions";

// ─── helpers ───────────────────────────────────────────────
const WEEKDAYS_FR = ["lundi","mardi","mercredi","jeudi","vendredi","samedi","dimanche"];
const DAY_SHORT   = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"];
const DAY_FULL    = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"];

const toISO = (d) => { const x=new Date(d); x.setHours(0,0,0,0); return x.toISOString().split("T")[0]; };

const mondayOf = (d) => {
    const x=new Date(d), day=x.getDay();
    x.setDate(x.getDate()-day+(day===0?-6:1)); x.setHours(0,0,0,0); return x;
};
const sundayOf  = (d) => { const s=mondayOf(d),e=new Date(s); e.setDate(s.getDate()+6); e.setHours(0,0,0,0); return e; };
const weekDaysOf = (ref) => Array.from({length:7},(_,i)=>{ const d=new Date(mondayOf(ref)); d.setDate(d.getDate()+i); d.setHours(0,0,0,0); return d; });

const monthGrid = (ref) => {
    const y=ref.getFullYear(),m=ref.getMonth();
    const first=new Date(y,m,1),last=new Date(y,m+1,0);
    let off=first.getDay()-1; if(off===-1)off=6;
    const weeks=[]; let cur=[];
    for(let i=0;i<off;i++) cur.push(null);
    for(let dd=1;dd<=last.getDate();dd++){
        if(cur.length===7){weeks.push(cur);cur=[];}
        const dt=new Date(y,m,dd); dt.setHours(0,0,0,0); cur.push(dt);
    }
    while(cur.length<7) cur.push(null); weeks.push(cur); return weeks;
};

const weekRelative = (ref) => {
    const t=mondayOf(new Date()).getTime(), r=mondayOf(ref).getTime();
    return r<t?-1:r>t?1:0;
};

// ─── composant ─────────────────────────────────────────────
const FeuillesTemps = () => {
    const dispatch = useDispatch();

    const { workdaysConfig, calendarSettings } = useSelector(s=>s.workDays||{});
    const { timesheets=[], currentTimesheet=null, daysByTimesheetId={}, loading=false, error=null }
        = useSelector(s=>s.timesheet||{});
    const user = useSelector(s=>s.user?.currentUser);

    const [activeTab,   setActiveTab]   = useState("saisie");
    const [viewMode,    setViewMode]    = useState("week");
    const [currentDate, setCurrentDate] = useState(new Date());
    const [searchTerm,  setSearchTerm]  = useState("");
    const [localHours,  setLocalHours]  = useState({});
    const [saving,      setSaving]      = useState(false);
    const [closing,     setClosing]     = useState(false);

    const today    = useMemo(()=>new Date(),[]);
    const todayISO = toISO(today);

    const rel       = useMemo(()=>weekRelative(currentDate),[currentDate]);
    const isCurrent = rel===0, isPast=rel===-1, isFuture=rel===1;

    const periodStart = useMemo(()=>{
        if(viewMode==="week") return toISO(mondayOf(currentDate));
        const d=new Date(currentDate.getFullYear(),currentDate.getMonth(),1); d.setHours(0,0,0,0); return toISO(d);
    },[viewMode,currentDate]);

    const periodEnd = useMemo(()=>{
        if(viewMode==="week") return toISO(sundayOf(currentDate));
        const d=new Date(currentDate.getFullYear(),currentDate.getMonth()+1,0); d.setHours(0,0,0,0); return toISO(d);
    },[viewMode,currentDate]);

    useEffect(()=>{ dispatch(getWorkdaysConfigRequest()); dispatch(fetchTimesheetsRequest()); },[]);
    useEffect(()=>{ dispatch(getCalendarSettingsRequest(currentDate.getFullYear(),"MA")); },[currentDate.getFullYear()]);
    useEffect(()=>{ setLocalHours({}); },[periodStart]);

    const workDaysMap = useMemo(()=>{
        const src=workdaysConfig?.workDays||{},out={};
        WEEKDAYS_FR.forEach(d=>{ const v=src[d]||{}; const iw=(v.isWorking??v.working??true)===true; out[d]={isWorking:iw,hours:iw?Number(v.hours??8):0}; });
        return out;
    },[workdaysConfig]);

    const fixed=calendarSettings?.fixed||[], hijri=calendarSettings?.hijri||[], custom=calendarSettings?.custom||[];

    const isFixedH  = d=>{ const m=String(d.getMonth()+1).padStart(2,"0"),dd=String(d.getDate()).padStart(2,"0"); return fixed.some(x=>x.key===`${m}-${dd}`&&!!x.selected); };
    const isHijriH  = d=>{ const iso=toISO(d); return hijri.some(h=>{ if(!h.selected||!h.date)return false; const s=new Date(h.date);s.setHours(0,0,0,0); for(let i=0;i<Number(h.duration||1);i++){const t=new Date(s);t.setDate(s.getDate()+i);if(toISO(t)===iso)return true;} return false; }); };
    const isCustomH = d=>custom.some(c=>c.date===toISO(d));
    const isNonWork = d=>{ let i=d.getDay();i=i===0?6:i-1; return workDaysMap[WEEKDAYS_FR[i]]?.isWorking===false; };
    const isOff     = d=>isFixedH(d)||isHijriH(d)||isCustomH(d)||isNonWork(d);

    const hLabel = d=>{
        const L=[],iso=toISO(d);
        const m=String(d.getMonth()+1).padStart(2,"0"),dd=String(d.getDate()).padStart(2,"0");
        const fx=fixed.find(x=>x.key===`${m}-${dd}`&&!!x.selected); if(fx)L.push(fx.label);
        hijri.forEach(h=>{ if(!h.selected||!h.date)return; const s=new Date(h.date);s.setHours(0,0,0,0); for(let i=0;i<Number(h.duration||1);i++){const t=new Date(s);t.setDate(s.getDate()+i);if(toISO(t)===iso){L.push(`${h.name} (J${i+1}/${h.duration||1})`);break;}} });
        const cu=custom.find(c=>c.date===iso); if(cu)L.push(cu.description);
        return L.join(" · ");
    };

    const normalH   = d=>{ let i=d.getDay();i=i===0?6:i-1; return Number(workDaysMap[WEEKDAYS_FR[i]]?.hours??0); };
    const isFutureDay = d=>{ const x=new Date(d);x.setHours(0,0,0,0); const t=new Date(today);t.setHours(0,0,0,0); return x.getTime()>t.getTime(); };

    const sheet       = useMemo(()=>(timesheets||[]).find(t=>t.periodStart===periodStart&&t.periodEnd===periodEnd)||null,[timesheets,periodStart,periodEnd]);
    const sheetId     = sheet?.id||(isCurrent?currentTimesheet?.id:null)||null;
    const WORKFLOW_STATUTS = ["SUBMITTED","PENDING_MANAGER","PENDING_ADMIN","APPROVED","REJECTED"];
    const isSubmitted = WORKFLOW_STATUTS.includes(String(sheet?.statut||"").toUpperCase());    const isDraft     = !!sheetId&&!isSubmitted;
    const isPeriodOver= periodEnd<todayISO;
    const isAutoLocked= isPast&&isPeriodOver&&!isSubmitted&&!!sheetId;
    const canEdit     = isCurrent&&!isSubmitted;

    useEffect(()=>{ if(sheetId)dispatch(fetchTimesheetDaysRequest(sheetId)); },[sheetId]);

    const backendRows   = useMemo(()=>sheetId?daysByTimesheetId[sheetId]||[]:[]  ,[sheetId,daysByTimesheetId]);
    const backendByDate = useMemo(()=>{ const m={}; backendRows.forEach(r=>{ if(r?.workDate)m[r.workDate]=r; }); return m; },[backendRows]);

    useEffect(()=>{
        if(!isCurrent||isSubmitted||backendRows.length===0) return;
        setLocalHours(prev=>{
            const next={...prev};
            backendRows.forEach(r=>{ if(r?.workDate&&!(r.workDate in next)) next[r.workDate]=Number(r.heures||0); });
            return next;
        });
    },[backendRows,isCurrent,isSubmitted]);

    const handleChange = (dateKey,dateObj,val)=>{
        if(!canEdit||isFutureDay(dateObj)) return;
        const max = isOff(dateObj) ? 24 : (normalH(dateObj) || 24);
        const num = Math.min(max, Math.max(0, Number(val) || 0));
        setLocalHours(prev=>({...prev,[dateKey]:num}));
    };

    const sendDays = (id)=>{
        // ⚠️ Envoi SÉQUENTIEL avec délai — évite le deadlock
        // Chaque upsertDay fait un recomputeTotals (UPDATE timesheet_saisie)
        // → 3 requêtes parallèles sur la même ligne = deadlock garanti
        const days = Object.entries(localHours)
            .filter(([k,h])=>h>0&&k<=todayISO)
            .sort(([a],[b])=>a.localeCompare(b)); // ordre chronologique

        days.forEach(([dateKey,heures], index)=>{
            const dateObj = new Date(dateKey);
            const off = isOff(dateObj);
            setTimeout(()=>{
                dispatch(upsertTimesheetDayRequest({
                    timesheetId: id,
                    workDate: dateKey,
                    heures,
                    commentaire: off ? "Overtime (jour non ouvré)" : "",
                }));
            }, index * 450); // 450ms entre chaque pour laisser la transaction se terminer
        });
    };

    const pendingAction=useRef(null), prevSheetId=useRef(null);

    const handleSave = ()=>{
        if(!canEdit||saving||closing) return;
        if(!Object.values(localHours).some(h=>h>0)) return;
        setSaving(true);
        if(!sheetId){
            pendingAction.current="save";
            dispatch(createTimesheetRequest({employeeId:user?.id,periodStart,periodEnd,statut:"DRAFT"}));
        } else {
            sendDays(sheetId);
            dispatch(updateTimesheetRequest(sheetId,{employeeId:sheet?.employeeId??user?.id,periodStart:sheet?.periodStart??periodStart,periodEnd:sheet?.periodEnd??periodEnd,statut:"DRAFT"}));
            setSaving(false);
        }
    };

    const handleClose = ()=>{
        if(!canEdit||saving||closing) return;
        setClosing(true);
        if(!sheetId){
            pendingAction.current="close";
            dispatch(createTimesheetRequest({employeeId:user?.id,periodStart,periodEnd,statut:"SUBMITTED"}));
        } else {
            sendDays(sheetId);
            dispatch(updateTimesheetRequest(sheetId,{employeeId:sheet?.employeeId??user?.id,periodStart:sheet?.periodStart??periodStart,periodEnd:sheet?.periodEnd??periodEnd,statut:"SUBMITTED"}));
            setClosing(false);
        }
    };

    useEffect(()=>{
        if(!pendingAction.current) return;
        const newId=currentTimesheet?.id;
        if(!newId||newId===prevSheetId.current) return;
        prevSheetId.current=newId;
        sendDays(newId);
        pendingAction.current=null;
        setSaving(false); setClosing(false);
    },[currentTimesheet]);

    const wDays = useMemo(()=>weekDaysOf(currentDate).map((d,i)=>({
        dateObj:d, dateKey:toISO(d),
        short:DAY_SHORT[i], full:DAY_FULL[i],
        isOff:isOff(d), hLabel:hLabel(d),
        normalHours:normalH(d),
        maxHours:isOff(d)?24:(normalH(d)||24),
        isToday:toISO(d)===todayISO,
        isFutureDay:isFutureDay(d),
    })),[currentDate,workDaysMap,calendarSettings,todayISO]);

    const mGrid = useMemo(()=>monthGrid(currentDate).map(wk=>wk.map(d=>d?({
        dateObj:d,dateKey:toISO(d),
        isOff:isOff(d),hLabel:hLabel(d),
        normalHours:normalH(d)||24,
        isToday:toISO(d)===todayISO,
        isFutureDay:isFutureDay(d),
    }):null)),[currentDate,workDaysMap,calendarSettings,todayISO]);

    const getVal = (dateKey)=>{
        if(isCurrent&&!isSubmitted) return localHours[dateKey]||0;
        return Number(backendByDate[dateKey]?.heures||0);
    };

    const totalSaisi = useMemo(()=>wDays.reduce((s,d)=>s+getVal(d.dateKey),0),[wDays,localHours,backendByDate,isCurrent,isSubmitted]);
    const totalTheo  = useMemo(()=>wDays.reduce((s,d)=>s+(d.isOff?0:d.normalHours),0),[wDays]);
    const completion = totalTheo>0?Math.min(100,Math.round(totalSaisi/totalTheo*100)):0;

    const goPrev = ()=>{ const d=new Date(currentDate); if(viewMode==="week")d.setDate(d.getDate()-7); else d.setMonth(d.getMonth()-1); setCurrentDate(d); };
    const goNext = ()=>{ const d=new Date(currentDate); if(viewMode==="week")d.setDate(d.getDate()+7); else d.setMonth(d.getMonth()+1); setCurrentDate(d); };

    const periodLabel = useMemo(()=>{
        if(viewMode==="week"){
            const s=new Date(periodStart).toLocaleDateString("fr-FR",{day:"2-digit",month:"short"});
            const e=new Date(periodEnd).toLocaleDateString("fr-FR",{day:"2-digit",month:"short",year:"numeric"});
            return `${s} — ${e}`;
        }
        return currentDate.toLocaleDateString("fr-FR",{month:"long",year:"numeric"});
    },[viewMode,periodStart,periodEnd,currentDate]);

    const statusInfo = useMemo(()=>{
        const st = String(sheet?.statut||"").toUpperCase();

        if(isFuture)                          return {label:"Semaine à venir",       cls:"future",          icon:<Lock size={11}/>};
        if(st==="APPROVED")                   return {label:"Approuvée",             cls:"submitted",       icon:<CheckCircle size={11}/>};
        if(st==="REJECTED")                   return {label:"Refusée",               cls:"missing",         icon:<XCircle size={11}/>};
        if(st==="PENDING_ADMIN")              return {label:"En attente RH",         cls:"draft-past",      icon:<AlertCircle size={11}/>};
        if(st==="PENDING_MANAGER")            return {label:"En attente manager",    cls:"draft-past",      icon:<AlertCircle size={11}/>};
        if(st==="SUBMITTED")                  return {label:"Soumise",               cls:"submitted",       icon:<CheckCircle size={11}/>};
        if(isAutoLocked)                      return {label:"Période expirée",       cls:"missing",         icon:<Lock size={11}/>};
        if(isPast&&!sheet)                    return {label:"Non saisie",            cls:"missing",         icon:<AlertCircle size={11}/>};
        if(isPast&&isDraft)                   return {label:"Brouillon",             cls:"draft-past",      icon:<Edit2 size={11}/>};
        if(isDraft)                           return {label:"Brouillon",             cls:"draft",           icon:<Edit2 size={11}/>};
        const has=Object.values(localHours).some(h=>h>0);
        if(has)                               return {label:"En cours",              cls:"active",          icon:<Edit2 size={11}/>};
        return                                       {label:"Nouvelle période",      cls:"new",             icon:<Edit2 size={11}/>};
    },[isFuture,isPast,isCurrent,isSubmitted,isAutoLocked,isDraft,sheet,localHours]);

    const hasLocalHours = Object.values(localHours).some(h=>h>0);

    const filteredHistory = useMemo(()=>{
        const t=(searchTerm||"").toLowerCase();
        if(!t) return timesheets;
        return timesheets.filter(f=>`${f.periodStart} ${f.periodEnd} ${f.statut}`.toLowerCase().includes(t));
    },[timesheets,searchTerm]);

    // ─── render ────────────────────────────────────────────
    return (
        <div className="ts-root">

            {/* TOP BAR */}
            <div className="ts-topbar">
                <div className="ts-topbar__left">
                    <div className="ts-logo"><Clock size={15}/></div>
                    <span className="ts-title">Feuilles de Temps</span>
                </div>
                <div className="ts-topbar__center">
                    <button className={`ts-tab${activeTab==="saisie"?" active":""}`} onClick={()=>setActiveTab("saisie")}>
                        <Edit2 size={13}/> Saisie
                    </button>
                    <button className={`ts-tab${activeTab==="historique"?" active":""}`} onClick={()=>setActiveTab("historique")}>
                        <FileText size={13}/> Historique
                        {timesheets.length>0&&<span className="ts-badge">{timesheets.length}</span>}
                    </button>
                </div>
                <div className="ts-topbar__right">
                    <div className="ts-search">
                        <FileText size={13}/>
                        <input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder="Rechercher…"/>
                        {searchTerm&&<button className="ts-search-x" onClick={()=>setSearchTerm("")}><X size={12}/></button>}
                    </div>
                </div>
            </div>

            <div className="ts-body">
                {activeTab==="saisie"&&(
                    <>
                        {/* ── NAV ── */}
                        <div className="ts-nav">
                            <div className="ts-toggle">
                                <button className={viewMode==="week"?"on":""} onClick={()=>setViewMode("week")}>Semaine</button>
                                <button className={viewMode==="month"?"on":""} onClick={()=>setViewMode("month")}>Mois</button>
                            </div>
                            <div className="ts-nav__center">
                                <button className="ts-arr" onClick={goPrev}><ChevronLeft size={16}/></button>
                                <div className="ts-period">
                                    {isCurrent&&<span className="ts-live-dot"/>}
                                    {periodLabel}
                                </div>
                                <button className="ts-arr" onClick={goNext}><ChevronRight size={16}/></button>
                            </div>
                            <div className={`ts-chip ts-chip--${statusInfo.cls}`}>
                                {statusInfo.icon}{statusInfo.label}
                            </div>
                        </div>

                        {/* ── BANNERS ── */}
                        {isFuture&&<div className="ts-banner ts-banner--future"><Lock size={14}/><span>Cette semaine n'est pas encore ouverte à la saisie.</span></div>}
                        {isSubmitted&&(()=>{
                            const st = String(sheet?.statut||"").toUpperCase();
                            const msgs = {
                                PENDING_MANAGER: { icon:<AlertCircle size={14}/>, txt:"En attente de validation par votre manager.", cls:"ts-banner--warn" },
                                PENDING_ADMIN:   { icon:<AlertCircle size={14}/>, txt:"En attente de validation par le service RH.", cls:"ts-banner--warn" },
                                APPROVED:        { icon:<CheckCircle size={14}/>, txt:"Feuille approuvée par RH.",                  cls:"ts-banner--ok"   },
                                REJECTED:        { icon:<XCircle size={14}/>,     txt:"Feuille refusée — contactez votre responsable.", cls:"ts-banner--err" },
                                SUBMITTED:       { icon:<CheckCircle size={14}/>, txt:"Feuille soumise — en cours de traitement.", cls:"ts-banner--ok"   },
                            };
                            const cfg = msgs[st] || msgs.SUBMITTED;
                            return <div className={`ts-banner ${cfg.cls}`}>{cfg.icon}<span>{cfg.txt}</span></div>;
                        })()}                        {isAutoLocked&&<div className="ts-banner ts-banner--warn"><Lock size={14}/><span>Période expirée. Brouillon non clôturé, lecture seule.</span></div>}
                        {isPast&&!sheet&&<div className="ts-banner ts-banner--warn"><AlertCircle size={14}/><span>Aucune saisie enregistrée pour cette semaine.</span></div>}
                        {isDraft&&isPast&&!isAutoLocked&&<div className="ts-banner ts-banner--draft"><AlertCircle size={14}/><span>Brouillon non clôturé — pensez à clôturer cette feuille.</span></div>}
                        {isCurrent&&!isSubmitted&&<div className="ts-banner ts-banner--info"><AlertCircle size={14}/><span>{isDraft?"Brouillon enregistré — complétez et mettez à jour ou clôturez.":"Saisissez vos heures. Rien n'est enregistré avant d'avoir cliqué Enregistrer."}</span></div>}

                        {/* ── KPIs ── */}
                        {viewMode==="week"&&(
                            <div className="ts-kpis">
                                <div className="ts-kpi">
                                    <div className="ts-kpi__icon --blue"><Calendar size={14}/></div>
                                    <div><p className="ts-kpi__lbl">Théorique</p><p className="ts-kpi__num">{totalTheo}<em>h</em></p></div>
                                </div>
                                <div className="ts-kpi">
                                    <div className="ts-kpi__icon --green"><TrendingUp size={14}/></div>
                                    <div><p className="ts-kpi__lbl">Saisi</p><p className="ts-kpi__num --green">{totalSaisi}<em>h</em></p></div>
                                </div>
                                <div className="ts-kpi">
                                    <div className="ts-kpi__icon --orange"><Zap size={14}/></div>
                                    <div><p className="ts-kpi__lbl">Restant</p><p className="ts-kpi__num --orange">{Math.max(0,totalTheo-totalSaisi)}<em>h</em></p></div>
                                </div>
                                <div className="ts-kpi ts-kpi--wide">
                                    <div className="ts-kpi__prog-head">
                                        <p className="ts-kpi__lbl">Complétion</p>
                                        <p className="ts-kpi__pct">{completion}%</p>
                                    </div>
                                    <div className="ts-track"><div className={`ts-fill${completion>=100?" --done":""}`} style={{width:`${completion}%`}}/></div>
                                </div>
                            </div>
                        )}

                        {/* ── WEEK VIEW : lignes horizontales ── */}
                        {viewMode==="week"&&(
                            <div className={`ts-rows${isFuture?" --locked":""}`}>
                                {wDays.map((day,i)=>{
                                    const val   = getVal(day.dateKey);
                                    const pct   = day.normalHours>0?Math.min(100,val/day.normalHours*100):0;
                                    const full  = !day.isOff&&val>=day.normalHours&&day.normalHours>0&&val>0;
                                    const locked= !canEdit||day.isFutureDay;

                                    return (
                                        <div key={i} className={[
                                            "ts-row",
                                            day.isToday&&isCurrent?"--today":"",
                                            day.isOff?"--off":"",
                                            day.isFutureDay?"--future-day":"",
                                            full&&!day.isOff?"--full":"",
                                            locked&&isCurrent&&!day.isOff&&day.isFutureDay?"--locked-day":"",
                                        ].filter(Boolean).join(" ")}>

                                            {/* Colonne gauche : date */}
                                            <div className="ts-row__date">
                                                <span className="ts-row__daynum">{day.dateObj.getDate()}</span>
                                                <div className="ts-row__daylabels">
                                                    <span className="ts-row__dayname">{day.full}</span>
                                                    <span className="ts-row__month">
                                                        {day.dateObj.toLocaleDateString("fr-FR",{month:"short",year:"numeric"})}
                                                    </span>
                                                </div>
                                                {day.isToday&&isCurrent&&<span className="ts-today-badge">Aujourd'hui</span>}
                                            </div>

                                            {/* Colonne centre : barre + statut */}
                                            <div className="ts-row__middle">
                                                {day.isOff?(
                                                    <div className="ts-row__off-label">
                                                        <AlertCircle size={12}/>
                                                        {day.hLabel||"Jour férié / non ouvré"}
                                                    </div>
                                                ):day.isFutureDay?(
                                                    <div className="ts-row__future-label">
                                                        <Lock size={11}/> Pas encore disponible
                                                    </div>
                                                ):(
                                                    <>
                                                        <div className="ts-row__bar-wrap">
                                                            <div className="ts-row__bar">
                                                                <div
                                                                    className={`ts-row__bar-fill${full?" --done":val>0?" --on":""}`}
                                                                    style={{width:`${pct}%`}}
                                                                />
                                                            </div>
                                                            <span className="ts-row__bar-label">
                                                                {val>0?`${val} / ${day.normalHours}h`:`0 / ${day.normalHours}h`}
                                                            </span>
                                                        </div>
                                                        {full&&(
                                                            <span className="ts-row__tag --done">
                                                                <CheckCircle size={10}/> Complet
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            {/* Colonne droite : input */}
                                            <div className="ts-row__right">
                                                {day.isFutureDay?(
                                                    <div className="ts-row__val --muted">—</div>
                                                ):canEdit?(
                                                    <div className="ts-row__input-group">
                                                        <input
                                                            className="ts-row__input"
                                                            type="number"
                                                            min="0"
                                                            max={day.normalHours}
                                                            value={val||""}
                                                            placeholder="0"
                                                            onChange={e=>handleChange(day.dateKey,day.dateObj,e.target.value)}
                                                        />
                                                        <span className="ts-row__unit">h</span>
                                                    </div>
                                                ):(
                                                    <div className={`ts-row__val${val>0?" --filled":""}`}>
                                                        {val>0?val:"—"}{val>0&&<span>h</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* LIGNE TOTAL */}
                                <div className="ts-row ts-row--total">
                                    <div className="ts-row__date">
                                        <span className="ts-row__daynum ts-row__daynum--total">{completion}%</span>
                                        <div className="ts-row__daylabels">
                                            <span className="ts-row__dayname">Total semaine</span>
                                            <span className="ts-row__month">{totalTheo}h théoriques</span>
                                        </div>
                                    </div>
                                    <div className="ts-row__middle">
                                        <div className="ts-row__bar-wrap">
                                            <div className="ts-row__bar ts-row__bar--thick">
                                                <div
                                                    className={`ts-row__bar-fill${completion>=100?" --done":" --on"}`}
                                                    style={{width:`${completion}%`}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="ts-row__right">
                                        <div className={`ts-row__val --big${completion>=100?" --done":""}`}>
                                            {totalSaisi}<span>h</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── MONTH VIEW ── */}
                        {viewMode==="month"&&(
                            <div className={`ts-month${isFuture?" --locked":""}`}>
                                <div className="ts-month__head">
                                    {DAY_SHORT.map(d=><div key={d}>{d}</div>)}
                                </div>
                                {mGrid.map((week,wi)=>(
                                    <div className="ts-month__row" key={wi}>
                                        {week.map((cell,ci)=>{
                                            if(!cell) return <div key={ci} className="ts-mc ts-mc--empty"/>;
                                            const cellMon  = toISO(mondayOf(cell.dateObj));
                                            const todayMon = toISO(mondayOf(today));
                                            const cellRel  = cellMon<todayMon?-1:cellMon>todayMon?1:0;
                                            const cellIsCur= cellRel===0;
                                            const val      = cellIsCur&&!isSubmitted?(localHours[cell.dateKey]||0):Number(backendByDate[cell.dateKey]?.heures||0);
                                            const cellEdit = cellIsCur&&!isSubmitted&&!cell.isFutureDay;

                                            return (
                                                <div key={ci} title={cell.hLabel||""} className={[
                                                    "ts-mc",
                                                    cell.isToday?"--today":"",
                                                    cell.isOff?"--off":"",
                                                    cellRel===1?"--future":"",
                                                    cellRel===-1?"--past":"",
                                                    cell.isFutureDay?"--day-future":"",
                                                ].filter(Boolean).join(" ")}>
                                                    <span className="ts-mc__dn">{cell.dateObj.getDate()}</span>
                                                    {cellEdit?(
                                                        <input
                                                            className="ts-mc__inp"
                                                            type="number" min="0" max={cell.normalHours}
                                                            value={val||""} placeholder="0"
                                                            onChange={e=>handleChange(cell.dateKey,cell.dateObj,e.target.value)}
                                                        />
                                                    ):(
                                                        <span className={`ts-mc__ro${val>0?" --has":""}`}>{val>0?`${val}h`:""}</span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ── ACTION ZONE ── */}
                        {isCurrent&&!isSubmitted&&(
                            <div className="ts-action-zone">
                                <span className="ts-action-hint">
                                    <AlertCircle size={13}/>
                                    {isDraft
                                        ? "Brouillon enregistré — complétez et mettez à jour, ou clôturez définitivement."
                                        : "Les heures non enregistrées seront perdues si vous quittez la page."}
                                </span>
                                <div className="ts-action-btns">
                                    <button className="ts-btn-save" onClick={handleSave} disabled={!hasLocalHours||saving||closing}>
                                        {saving?<><div className="ts-spin-sm"/>Enregistrement…</>:isDraft?<><RefreshCw size={14}/> Mettre à jour</>:<><Save size={14}/> Enregistrer</>}
                                    </button>
                                    <button className="ts-btn-close" onClick={handleClose} disabled={!hasLocalHours||saving||closing}>
                                        {closing?<><div className="ts-spin-sm --dark"/>Clôture…</>:<><Lock size={14}/> Clôturer</>}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ── HISTORIQUE ── */}
                {activeTab==="historique"&&(
                    <div className="ts-hist">
                        {filteredHistory.length===0?(
                            <div className="ts-empty"><FileText size={26}/><span>Aucune feuille trouvée</span></div>
                        ):filteredHistory.map(f=>{
                            const st=String(f.statut||"DRAFT").toUpperCase();
                            return (
                                <div key={f.id} className="ts-hr">
                                    <div className="ts-hr__av"><FileText size={15}/></div>
                                    <div className="ts-hr__main">
                                        <p className="ts-hr__period">{f.periodStart} → {f.periodEnd}</p>
                                        <p className="ts-hr__id">#{f.id}</p>
                                    </div>
                                    <div className="ts-hr__meta">
                                        {(()=>{
                                            const badges = {
                                                DRAFT:           { cls:"--draft",   icon:<Clock size={11}/>,        label:"Brouillon"          },
                                                SUBMITTED:       { cls:"--ok",      icon:<CheckCircle size={11}/>,  label:"Soumise"            },
                                                PENDING_MANAGER: { cls:"--pending", icon:<AlertCircle size={11}/>,  label:"Attente manager"    },
                                                PENDING_ADMIN:   { cls:"--pending", icon:<AlertCircle size={11}/>,  label:"Attente RH"         },
                                                APPROVED:        { cls:"--ok",      icon:<CheckCircle size={11}/>,  label:"Approuvée"          },
                                                REJECTED:        { cls:"--err",     icon:<XCircle size={11}/>,      label:"Refusée"            },
                                            };
                                            const b = badges[st] || badges.DRAFT;
                                            return (
                                                <span className={`ts-spill ${b.cls}`}>
            {b.icon}{b.label}
        </span>
                                            );
                                        })()}
                                        <span className="ts-hchip">{Number(f.totalHeures||0)}h</span>
                                        {Number(f.heuresSupp||0)>0&&<span className="ts-hchip --ora">+{Number(f.heuresSupp||0)}h supp.</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {error&&<div className="ts-toast --err"><XCircle size={14}/>{String(error)}</div>}
            {(loading||saving||closing)&&<div className="ts-toast --load"><div className="ts-spin"/>Chargement…</div>}
        </div>
    );
};

export default FeuillesTemps;